<script setup lang="ts">
    import type { SelectOption } from '@/types';
    import type { Table } from '@tanstack/vue-table';
    import {
        Select,
        SelectContent,
        SelectGroup,
        SelectItem,
        SelectTrigger,
        SelectValue,
    } from '@/components/ui/select';
    import { computed, ref } from 'vue';
    import { useI18n } from 'vue-i18n';
    import { toast } from 'vue-sonner';
    import type { AcceptableValue } from 'reka-ui';
    import Indicator from '@/components/Indicator.vue';
    import axios from 'axios';

    const { t } = useI18n();

    

    const props = defineProps<{
        table: Table<any>;
        options: SelectOption[];
        dataFieldGroup: string;
        reportingPeriod: string;
        title: string;
    }>();

    const title = computed(() => {
        return t(props.title);
    });

    const selectedValue = ref<AcceptableValue | null>(null);

    const companyIds = computed(() => {
        return props.table.getFilteredSelectedRowModel().flatRows.map((row) => row.original.id);
    });

    const hasSelectedRows = computed(() => {
        return companyIds.value.length > 0;
    });

    const routeName = computed(() => {
        return props.dataFieldGroup === 'vat_status'
            ? 'v2.dashboard.vat.bulk-update'
            : 'v2.dashboard.status.bulk-update';
    });

    const handleBulkUpdate = async () => {
        try {
            const selectedOption = props.options.find((opt) => opt.value === selectedValue.value);
            if (!selectedOption || !hasSelectedRows.value) return;

            await axios.post(route(routeName.value), {
                reporting_period: props.reportingPeriod,
                companies: companyIds.value,
                data_field_id: selectedOption.value,
                data_field_group: props.dataFieldGroup,
                data_field_approve: selectedOption.addon,
            });

            // Trigger a manual table refetch with loader in Dashboard
            window.dispatchEvent(new CustomEvent('dashboard-manual-refetch'));

            toast.success(
                t('dashboards.bulk_update_success', {
                    status: title.value,
                    count: companyIds.value.length,
                }),
            );
        } catch (error) {
            console.error('Failed to update status:', error);
            toast.error(t('dashboards.status_update_failed'));
        }
    };

    const handleSelectChange = (value: AcceptableValue) => {
        selectedValue.value = value;
        if (value) {
            handleBulkUpdate();
        }
    };
</script>

<template>
    <Select
        :model-value="selectedValue"
        @update:model-value="handleSelectChange"
    >
        <SelectTrigger class="data-[placeholder]:text-primary border shadow-none">
            <SelectValue>
                <span class="flex items-center gap-1.5"> {{ title }} </span>
            </SelectValue>
        </SelectTrigger>
        <SelectContent>
            <SelectGroup>
                <SelectItem
                    v-for="option in options"
                    :key="option.value?.toString()"
                    :value="option.value"
                >
                    <span class="flex items-center gap-1.5">
                        <Indicator :variant="option.color" />
                        {{ option.label }}
                    </span>
                </SelectItem>
            </SelectGroup>
        </SelectContent>
    </Select>
</template>
